import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Group, Select, Stack, Text, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { sortBy } from "lodash-es";
import { z } from "zod";

import {
  getGetEmployeesQueryKey,
  useCreateEmployee,
} from "@/shared/api/generated/employee";
import { useGetLocations } from "@/shared/api/generated/locations";
import { GetLocationsItem } from "@/shared/api/generated/schemas";
import { emptyStringToNull } from "@/shared/utils/forms/emptyStringToNull";

function useFormSchema() {
  const { t } = useLingui();

  return z.object({
    firstName: z.string().min(1, { message: t`Obligatorisk` }),
    lastName: z.string(),
    email: z
      .string()
      .email({ message: t`Ugyldig epostadresse` })
      .min(1, { message: t`Obligatorisk` }),
    phoneNumber: z.string(),
    location: z
      .string()
      .min(1, { message: t`Velg en lokasjon` })
      .nullable(),
  });
}

type FormInitial = z.input<ReturnType<typeof useFormSchema>>;
type FormSubmit = z.output<ReturnType<typeof useFormSchema>>;

interface Props {
  close: () => void;
  availableLocations?: GetLocationsItem[];
}

export default function CreateEmployeeForm({
  close,
  availableLocations,
}: Props) {
  const { museumId } = useParams({ strict: false });

  const { t } = useLingui();

  const queryClient = useQueryClient();

  const { data: allLocations } = useGetLocations({
    query: { enabled: availableLocations === undefined },
  });

  const locations = availableLocations ?? allLocations?.data;

  const { mutate: createEmployee } = useCreateEmployee({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getGetEmployeesQueryKey(),
        });
        close();
      },
    },
  });

  const formSchema = useFormSchema();

  const form = useForm<FormInitial, (v: FormInitial) => FormSubmit>({
    mode: "uncontrolled",
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      location: museumId ?? null,
    },
    validate: zodResolver(formSchema),
  });

  function handleSubmit(values: FormSubmit) {
    createEmployee({
      data: {
        firstName: values.firstName,
        lastName: emptyStringToNull(values.lastName),
        email: values.email,
        phoneNumber: emptyStringToNull(values.phoneNumber),
        locations: values.location ? [values.location] : null,
      },
    });
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Text size="sm" c="dimmed" mb={8}>
        <Trans>Fyll ut den informasjonen du har tilgjengelig.</Trans>
      </Text>

      <Stack>
        <Group grow align="flex-start">
          <TextInput
            label={t`Fornavn`}
            placeholder={t`Fornavn`}
            withAsterisk
            {...form.getInputProps("firstName")}
            key={form.key("firstName")}
          />
          <TextInput
            label={t`Etternavn`}
            placeholder={t`Etternavn`}
            {...form.getInputProps("lastName")}
            key={form.key("lastName")}
          />
        </Group>

        <Group grow align="flex-start">
          <TextInput
            label={t`Epostadresse`}
            placeholder={t`Epostadresse`}
            withAsterisk
            {...form.getInputProps("email")}
            key={form.key("email")}
          />

          <TextInput
            label={t`Telefonnummer`}
            placeholder={t`Telefonnummer`}
            {...form.getInputProps("phoneNumber")}
            key={form.key("phoneNumber")}
          />
        </Group>

        {!museumId && (
          <Select
            label={t`Lokasjon`}
            placeholder={t`Velg lokasjon`}
            data={
              sortBy(
                locations?.map((location) => ({
                  value: location.id,
                  label: location.name,
                })),
                (o) => o.label.toLowerCase(),
              ) ?? []
            }
            searchable
            clearable
            {...form.getInputProps("location")}
            key={form.key("location")}
          />
        )}

        <Group justify="end" gap={8} mt={8}>
          <Button variant="subtle" onClick={close}>
            <Trans>Avbryt</Trans>
          </Button>
          <Button type="submit">
            <Trans>Opprett</Trans>
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
